import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api-client";
import { FileText, Loader2, X } from "lucide-react";
import type { Claim } from "@/api/generated/client/models";

interface SearchResult { id: number; label: string }

/**
 * ClaimsPicker for create dialogs — manages a list of selected claim IDs.
 */
export function CreateClaimsPicker({
    selectedIds,
    onChange,
}: {
    selectedIds: number[];
    onChange: (ids: number[]) => void;
}) {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const labelCache = useRef<Map<number, string>>(new Map());

    useEffect(() => {
        if (!search) { setResults([]); setIsSearching(false); return; }
        setIsSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiClient.claims.listClaims({ search, limit: 15 });
                const claims = (res.claims || [])
                    // Only allow linking claims the user can edit; read-only shares are hidden.
                    .filter((c: Claim) => (c.canWrite ?? false))
                    .filter((c: Claim) => !selectedIds.includes(c.id))
                    .map((c: Claim) => ({
                        id: c.id,
                        label: [c.insuredName, c.claimNumber].filter(Boolean).join(" – ") || `Claim #${c.id}`,
                    }));
                setResults(claims);
            } catch { setResults([]); }
            setIsSearching(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, selectedIds]);

    const selectedLabels = selectedIds.map(id => ({ id, label: labelCache.current.get(id) || `Claim #${id}` }));

    return (
        <div className="space-y-3">
            <Label className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                Link Claims
            </Label>
            {selectedLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedLabels.map(({ id, label }) => (
                        <Badge key={id} variant="secondary" className="gap-1 pr-1">
                            {label}
                            <button
                                type="button"
                                aria-label={`Remove ${label}`}
                                onClick={() => onChange(selectedIds.filter((sid) => sid !== id))}
                                className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
            <div>
                <div className="relative">
                    <Input
                        placeholder="Search claims..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-8 text-sm pr-8"
                    />
                    {isSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground animate-spin" />}
                </div>
                {!isSearching && search && results.length > 0 && (
                    <div className="border rounded-md max-h-32 overflow-y-auto overflow-x-hidden mt-1">
                        {results.slice(0, 8).map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2 min-w-0"
                                onClick={() => {
                                    labelCache.current.set(c.id, c.label);
                                    onChange([...selectedIds, c.id]);
                                    setSearch("");
                                }}
                            >
                                <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span className="truncate">{c.label}</span>
                            </button>
                        ))}
                    </div>
                )}
                {!isSearching && search && results.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">No matching claims</p>
                )}
            </div>
        </div>
    );
}
